// Warehouse — modern indoor storage warehouse.
// Rebuild focus: tall shelving racks with visible pallets, loading dock, roof
// trusses, forklift, catwalk, floor safety lines.
import EnvironmentAtmosphere from "@/game/systems/Environment";
import {
    ConcreteWall,
    Crate,
    ShippingContainer,
    Barrel,
    Fence,
    Pipe,
    Ground,
    StreetLamp,
} from "@/game/systems/props";
import { MAT } from "@/game/systems/materials";

export const WAREHOUSE_WALLS = [
    // Perimeter
    { min: [-25, 0, -25], max: [-24, 4, 25] },
    { min: [24, 0, -25], max: [25, 4, 25] },
    { min: [-25, 0, -25], max: [25, 4, -24] },
    { min: [-25, 0, 24], max: [25, 4, 25] },
    // Central shelving row
    { min: [-1, 0, -12], max: [1, 3.4, -4] },
    { min: [-1, 0, 4], max: [1, 3.4, 12] },
    // Left shelves
    { min: [-14, 0, -14], max: [-12, 3.4, -6] },
    { min: [-14, 0, 6], max: [-12, 3.4, 14] },
    { min: [-8, 0, -14], max: [-6, 3.4, -6] },
    { min: [-8, 0, 6], max: [-6, 3.4, 14] },
    // Right shelves
    { min: [6, 0, -14], max: [8, 3.4, -6] },
    { min: [6, 0, 6], max: [8, 3.4, 14] },
    { min: [12, 0, -14], max: [14, 3.4, -6] },
    { min: [12, 0, 6], max: [14, 3.4, 14] },
    // Loading crates
    { min: [-4, 0, 16], max: [-2, 1.6, 18] },
    { min: [2, 0, 16], max: [4, 1.6, 18] },
    { min: [-4, 0, -18], max: [-2, 1.6, -16] },
    { min: [2, 0, -18], max: [4, 1.6, -16] },
];

export const WAREHOUSE_SPAWNS = {
    player: { position: [0, 1.7, 20], yaw: Math.PI },
    enemies: [[0, 1.7, -20], [-10, 1.7, -18], [10, 1.7, -18], [-18, 1.7, -14], [18, 1.7, -14]],
    allies: [[-4, 1.7, 20], [4, 1.7, 20], [-10, 1.7, 20], [10, 1.7, 20]],
};

export const WAREHOUSE_WAYPOINTS = [
    [0, 0, -14], [-10, 0, -10], [10, 0, -10],
    [-4, 0, -2], [4, 0, -2], [-18, 0, 0], [18, 0, 0],
    [-4, 0, 2], [4, 0, 2], [-10, 0, 10], [10, 0, 10], [0, 0, 14],
];

export const WAREHOUSE_META = {
    id: "warehouse",
    name: "Warehouse",
    walls: WAREHOUSE_WALLS,
    spawns: WAREHOUSE_SPAWNS,
    waypoints: WAREHOUSE_WAYPOINTS,
    bounds: { minX: -24, maxX: 24, minZ: -24, maxZ: 24 },
    preview: {
        environment: "INDOOR STORAGE",
        recommendedPlayers: "6 – 10",
        difficulty: "INTERMEDIATE",
        palette: ["#4a6b5a", "#3a3c40", "#a67a44"],
        tagline: "Tall shelving lanes, loading dock, close-in warehouse combat.",
    },
};

/** Renders one storage shelving rack — a tall unit with pallets */
function ShelfRack({ position, rotation = [0, 0, 0], size = [2, 3.4, 8], shadows }) {
    const [sx, sy, sz] = size;
    return (
        <group position={position} rotation={rotation}>
            {/* Frame */}
            <mesh castShadow={shadows} receiveShadow material={MAT.metalDark()}>
                <boxGeometry args={[sx, sy, sz]} />
            </mesh>
            {/* Wooden pallets on 3 shelves */}
            {[0.4, 1.6, 2.8].map((y, i) => (
                <group key={i} position={[0, y, 0]}>
                    {[-sz / 2 + 1.5, 0, sz / 2 - 1.5].map((z, j) => (
                        <mesh key={j} position={[0, 0, z]} material={MAT.wood()}>
                            <boxGeometry args={[sx * 0.85, 0.15, sz / 4]} />
                        </mesh>
                    ))}
                </group>
            ))}
        </group>
    );
}

export default function Warehouse({ shadows, timeOfDay = "day", weather = "sunny" }) {
    return (
        <group>
            <EnvironmentAtmosphere timeOfDay={timeOfDay} weather={weather} shadows={shadows} />

            {/* Polished concrete floor */}
            <Ground material="concreteLight" size={80} />
            {/* Yellow safety lines */}
            {[-20, -10, 10, 20].map((z) => (
                <mesh key={z} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.005, z]} material={MAT.metalYellow()}>
                    <planeGeometry args={[45, 0.12]} />
                </mesh>
            ))}
            {[-20, -10, 10, 20].map((x) => (
                <mesh key={`v${x}`} rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.005, 0]} material={MAT.metalYellow()}>
                    <planeGeometry args={[0.12, 45]} />
                </mesh>
            ))}

            {/* Perimeter walls — corrugated metal look */}
            <ConcreteWall position={[-24.5, 2, 0]} size={[1, 4, 50]} color="metalDark" stripe={false} shadows={shadows} />
            <ConcreteWall position={[24.5, 2, 0]} size={[1, 4, 50]} color="metalDark" stripe={false} shadows={shadows} />
            <ConcreteWall position={[0, 2, -24.5]} size={[50, 4, 1]} color="metalDark" stripe={false} shadows={shadows} />
            <ConcreteWall position={[0, 2, 24.5]} size={[50, 4, 1]} color="metalDark" stripe={false} shadows={shadows} />

            {/* Roof trusses — visible metal beams */}
            {[-15, -5, 5, 15].map((x) => (
                <group key={`truss-${x}`}>
                    <mesh position={[x, 5.5, 0]} material={MAT.metalDark()}>
                        <boxGeometry args={[0.2, 0.4, 48]} />
                    </mesh>
                    {[-20, -10, 0, 10, 20].map((z) => (
                        <mesh key={z} position={[x, 5.5, z]} material={MAT.metalDark()}>
                            <boxGeometry args={[0.3, 0.15, 0.2]} />
                        </mesh>
                    ))}
                </group>
            ))}

            {/* Overhead cross braces */}
            {[-20, -10, 0, 10, 20].map((z) => (
                <mesh key={`brace-${z}`} position={[0, 6, z]} material={MAT.metalDark()}>
                    <boxGeometry args={[48, 0.2, 0.15]} />
                </mesh>
            ))}

            {/* Central back-to-back shelving row */}
            <ShelfRack position={[0, 1.7, -8]} shadows={shadows} />
            <ShelfRack position={[0, 1.7, 8]} shadows={shadows} />

            {/* Left / right shelving lanes */}
            <ShelfRack position={[-13, 1.7, -10]} shadows={shadows} />
            <ShelfRack position={[-13, 1.7, 10]} shadows={shadows} />
            <ShelfRack position={[-7, 1.7, -10]} shadows={shadows} />
            <ShelfRack position={[-7, 1.7, 10]} shadows={shadows} />
            <ShelfRack position={[7, 1.7, -10]} shadows={shadows} />
            <ShelfRack position={[7, 1.7, 10]} shadows={shadows} />
            <ShelfRack position={[13, 1.7, -10]} shadows={shadows} />
            <ShelfRack position={[13, 1.7, 10]} shadows={shadows} />

            {/* Loading dock crates */}
            <Crate position={[-3, 0.8, 17]} size={[2, 1.6, 2]} shadows={shadows} />
            <Crate position={[3, 0.8, 17]} size={[2, 1.6, 2]} shadows={shadows} />
            <Crate position={[-3, 0.8, -17]} size={[2, 1.6, 2]} shadows={shadows} />
            <Crate position={[3, 0.8, -17]} size={[2, 1.6, 2]} shadows={shadows} />

            {/* Forklift in the aisle */}
            <group position={[-3, 0.7, 0]}>
                <mesh castShadow={shadows} material={MAT.metalYellow()}>
                    <boxGeometry args={[1.6, 1.4, 2.4]} />
                </mesh>
                {/* Cab */}
                <mesh position={[0, 1.3, 0.4]} material={MAT.metalYellow()}>
                    <boxGeometry args={[1.4, 1, 1.4]} />
                </mesh>
                {/* Mast */}
                <mesh position={[0, 1.4, -1.4]} material={MAT.metalDark()}>
                    <boxGeometry args={[0.2, 2.8, 0.15]} />
                </mesh>
                {/* Forks */}
                <mesh position={[0, -0.55, -1.7]} material={MAT.metalDark()}>
                    <boxGeometry args={[0.9, 0.1, 0.6]} />
                </mesh>
                {/* Wheels */}
                {[[-0.7, -0.65, 0.8], [0.7, -0.65, 0.8], [-0.7, -0.65, -0.8], [0.7, -0.65, -0.8]].map(([x, y, z], i) => (
                    <mesh key={i} position={[x, y, z]} rotation={[0, 0, Math.PI / 2]} material={MAT.metalDark()}>
                        <cylinderGeometry args={[0.25, 0.25, 0.2, 8]} />
                    </mesh>
                ))}
            </group>

            {/* Barrels stacked in corner */}
            <Barrel position={[-21, 0.55, -21]} color="metalRed" shadows={shadows} />
            <Barrel position={[-20, 0.55, -21]} color="metalRust" shadows={shadows} />
            <Barrel position={[-21, 0.55, -20]} color="metalYellow" shadows={shadows} />
            <Barrel position={[21, 0.55, 21]} color="metalRed" shadows={shadows} />
            <Barrel position={[21, 0.55, 20]} color="metalRust" shadows={shadows} />

            {/* Overhead pipe ducts */}
            <Pipe position={[0, 5.8, -20]} rotation={[Math.PI / 2, 0, 0]} length={8} radius={0.3} />
            <Pipe position={[0, 5.8, 20]} rotation={[Math.PI / 2, 0, 0]} length={8} radius={0.3} />

            {/* Ceiling lamps — cool white LED strips */}
            {[-16, -8, 0, 8, 16].map((z) =>
                [-14, 0, 14].map((x) => (
                    <group key={`lamp-${x}-${z}`} position={[x, 5.8, z]}>
                        <mesh material={MAT.lightCool()}>
                            <boxGeometry args={[3, 0.06, 0.4]} />
                        </mesh>
                        <pointLight position={[0, -0.3, 0]} intensity={0.5} color="#e0e6ff" distance={10} decay={2} />
                    </group>
                ))
            )}

            {/* Loading dock roll-up doors on north wall */}
            <mesh position={[-8, 2.2, -24.4]} material={MAT.metalRust()}>
                <boxGeometry args={[4, 4.2, 0.15]} />
            </mesh>
            <mesh position={[8, 2.2, -24.4]} material={MAT.metalRust()}>
                <boxGeometry args={[4, 4.2, 0.15]} />
            </mesh>
        </group>
    );
}
